import { bind, wire } from 'hyperhtml/umd';
import { _HTMLElement } from '../utils';
import { withAttributeReflection } from '../mixins/attribute-reflection';
import { withLifecycle } from '../mixins/lifecycle';
import { withShadowDOM } from '../mixins/shadow-dom';
import { withUpdate } from '../mixins/update';

/**
 * The `withViewComponent` compound mixin is intended for components requiring complex rendering that is difficult to achieve using manual DOM manipulation. To this end, it provides a `render` method (currently backed by [`hyperHTML`](https://github.com/WebReflection/hyperHTML)).
 *
 * **Mixins**
 * - `withAttributeReflection`
 * - `withLifecycle`
 * - `withShadowDOM`
 * - `withUpdate`
 *
 * @category mixin - compound
 * @param {String} styles CSS string that's shimmed for ShadowDOM use and
 * automatically attached to the ShadowDOM for each instance.
 * @param {Class} [Base=HTMLElement] Optional base class to extend from.
 * @returns {Class}
 *
 * @example
```js
import { withViewComponent } from '@updater-legacy/components/custom-element';
import styles from './index.css';

class List extends withViewComponent(styles)() {
  static props = ['value'];
  static reflectedAttributes = [
    { 
      name: 'disabled',
      boolean: true,
    },
    {
      name: 'type',
    }
  ];

  updated({ value }) {
    this.render`
      <ul>
        ${value.map({ id, label }) =>
          // As a convenience, this mixin provides a specialization of `hyperHTML`'s
          // `wire` method, with the added ability to re-use DOM nodes by string id.
          // It's useful when creating lists.
          this.wire(id)`<li>${label}</li>`
        )}
      </ul>
    `;
  }
}
 ```
 */
export const withViewComponent = (styles) => (Base = _HTMLElement) =>
  class extends withShadowDOM(styles)(
    withUpdate(withAttributeReflection(withLifecycle(Base)))
  ) {
    _wires = {};

    constructor() {
      super();

      // Calling `hyper` with a template string will replace whatever's
      // in the `shadowRoot` with DOM nodes created by `hyperHTML`.
      const hyper = bind(this.shadowRoot);
      const styles = wire()`${this._styles}`;

      const stringsWrapper = [''];

      // Wrap `hyper` to automatically insert ShadowDOM CSS before the
      // generated DOM so that we don't have to do so manually.
      this.render = (strings, ...expressions) => {
        // If the `strings` array passed to `hyper` isn't the same instance
        // every time, DOM node caching doesn't work.
        stringsWrapper.splice(1, 0, ...strings);
        hyper.apply(
          hyper,
          [stringsWrapper].concat([styles].concat(expressions))
        );
      };
    }

    /**
     * Specialized version of `hyperHTML`s `wire` method, supporting string ids
     * to cache DOM nodes for re-use. Useful for creating lists.
     * https://github.com/WebReflection/hyperHTML/issues/93
     *
     * @param {String} [id] Optional string id used to cache and re-use DOM nodes.
     * @return {Function} Tag function accepting a hyperHTML template string.
     *
     * @ignore (Hide from generated docs)
     */
    wire(id) {
      return id ? this._wires[id] || (this._wires[id] = wire()) : wire();
    }

    disconnectedCallback() {
      super.disconnectedCallback && super.disconnectedCallback();

      // Clear `wire` cache to avoid memory leaks.
      this._wires = {};
    }
  };
